// TallyKeyboardContext — finance-scoped function-key layer.
//
// Any finance screen calls useTallyKeys([{ key, label, hint, color, onPress }]).
// All currently-mounted finance components contribute keys simultaneously
// (merged, last-writer-wins on collisions) so e.g. TallyShell's Esc and a page's
// F5 are both live at once. The keydown handler reads a ref (always fresh
// closures); the button bar reads state gated by a signature (re-renders only
// when the visible key set changes — never every render, so no setState loop).
//
// Safe to mount app-wide: when no finance screen has registered keys the stack
// is empty and keydown returns early, so browser defaults (incl. F5 reload)
// behave normally everywhere except finance screens.

import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';

const Ctx = createContext(null);

const DISPLAY_ORDER = ['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12', 'Escape'];
const orderIdx = k => { const i = DISPLAY_ORDER.indexOf(k); return i === -1 ? 50 : i; };

function mergeFrames(frames) {
  const map = new Map();
  for (const f of frames) for (const e of f.entries) map.set(e.key, e); // later frame wins
  return Array.from(map.values()).sort((a, b) => orderIdx(a.key) - orderIdx(b.key));
}

// Signature of the *visible* button set (key + label). Stable across renders
// even though onPress closures change identity every render.
const signatureOf = entries => entries.map(e => `${e.key}:${e.label}`).join('|');

let _frameSeq = 0;

export function TallyKeyboardProvider({ children }) {
  const framesRef = useRef([]);          // [{ id, entries }]
  const sigRef = useRef('');
  const [buttons, setButtons] = useState([]); // display-only: { key, label, hint, color }

  const recompute = useCallback(() => {
    const merged = mergeFrames(framesRef.current);
    const sig = signatureOf(merged);
    if (sig !== sigRef.current) {
      sigRef.current = sig;
      setButtons(merged.map(({ key, label, hint, color }) => ({ key, label, hint, color })));
    }
  }, []);

  const pushFrame = useCallback((frame) => {
    framesRef.current = [...framesRef.current, frame];
    recompute();
  }, [recompute]);

  const updateFrame = useCallback((id, entries) => {
    framesRef.current = framesRef.current.map(f => (f.id === id ? { ...f, entries } : f));
    recompute();
  }, [recompute]);

  const popFrame = useCallback((id) => {
    framesRef.current = framesRef.current.filter(f => f.id !== id);
    recompute();
  }, [recompute]);

  // Invoke the freshest onPress for a key (used by both keyboard + button clicks).
  const press = useCallback((key, e) => {
    const entry = mergeFrames(framesRef.current).find(x => x.key === key);
    if (entry && entry.onPress) { entry.onPress(e); return true; }
    return false;
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (!framesRef.current.length) return;            // nothing registered → browser default
      const merged = mergeFrames(framesRef.current);
      const entry = merged.find(x => x.key === e.key);
      if (entry) { e.preventDefault(); entry.onPress && entry.onPress(e); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <Ctx.Provider value={{ pushFrame, updateFrame, popFrame, press, buttons }}>
      {children}
    </Ctx.Provider>
  );
}

// Register this screen's keys. Re-registers the latest entries every render so
// onPress closures never go stale — no need for callers to manage a deps array.
export function useTallyKeys(entries) {
  const ctx = useContext(Ctx);
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = ++_frameSeq;

  useEffect(() => {
    if (!ctx) return undefined;
    const id = idRef.current;
    ctx.pushFrame({ id, entries });
    return () => ctx.popFrame(id);
    // mount/unmount only — entries are refreshed every render by the effect below
  }, []);

  // Runs after every render → freshest closures in the ref (cheap; only
  // setState-s through recompute when the visible key set actually changes).
  useEffect(() => {
    if (ctx) ctx.updateFrame(idRef.current, entries);
  });
}

// ButtonBar reads this: display-only button list + a press() to invoke handlers.
export function useTallyButtons() {
  const ctx = useContext(Ctx);
  return { buttons: ctx?.buttons || [], press: ctx?.press || (() => false) };
}
